import React, { forwardRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';
interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}
const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-md hover:shadow-lg hover:shadow-blue-500/20 font-semibold',
  secondary: 'bg-[#1e1e1e] text-white hover:bg-[#2a2a2a] active:bg-[#333333] border border-[#2a2a2a]',
  outline: 'border-2 border-[#2a2a2a] text-white hover:bg-[#1a1a1a] hover:border-blue-500/50 active:bg-[#1e1e1e]',
  ghost: 'text-gray-400 hover:bg-[#1a1a1a] hover:text-white active:bg-[#1e1e1e]'
};
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-5 py-2.5 text-base gap-2',
  lg: 'px-7 py-3.5 text-lg gap-2.5'
};
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ...props
}, ref) => {
  return <motion.button ref={ref} whileTap={{
    scale: 0.98
  }} transition={{
    duration: 0.1
  }} className={`
          inline-flex items-center justify-center font-medium rounded-xl
          transition-all duration-200 focus:outline-none focus-visible:ring-2 
          focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `} disabled={disabled || loading} {...props} data-id="element-309">
        {loading ? <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-310">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-311" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-312" />
          </svg> : <>
            {leftIcon && <span className="flex-shrink-0" data-id="element-313">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0" data-id="element-314">{rightIcon}</span>}
          </>}
      </motion.button>;
});
Button.displayName = 'Button';